import { useEffect, useState } from "react";
import { Search, FileDown, UserX, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { BackupPanel, saveBlob } from "@/components/BackupPanel";

const box = "rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-4";
const GROUPS = [["appointments", "appointment_ids", "Appuntamenti / contratti"], ["impegnative", "impegnativa_ids", "Impegnative"], ["customers", "customer_ids", "Schede cliente"]];

function Results({ res, sel, toggle }) {
  return GROUPS.map(([key, , title]) => (res[key] || []).length > 0 && (
    <div key={key} className="space-y-1" data-testid={`privacy-group-${key}`}>
      <p className="eyebrow">{title}</p>
      {res[key].map((r) => (
        <label key={r.id} className="flex items-center gap-3 text-sm py-1 cursor-pointer">
          <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} data-testid={`privacy-check-${r.id}`} className="h-4 w-4" />
          <span className="truncate">{r.number ? `${r.number} · ` : ""}<b>{r.name}</b> {r.phone ? `· ${r.phone}` : ""} {r.date ? `· ${r.date}` : ""} {r.status ? `· ${r.status}` : ""}</span>
        </label>
      ))}
    </div>
  ));
}

function RetentionCard({ onShow }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const saveYears = async () => {
    try { setS((await api.put("/settings", { ...s, retention_years: Number(s.retention_years) || 0 })).data); toast.success("Periodo di conservazione salvato"); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className={box} data-testid="retention-card">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Clock className="h-5 w-5 text-[var(--primary)]" /> Periodo di conservazione</h2>
      <p className="text-sm text-[var(--muted)]">Dopo quanti anni i dati di un cliente possono essere anonimizzati (0 = nessun limite). Verifica con il tuo commercialista gli obblighi di conservazione fiscale prima di sceglierlo.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <Input type="number" min="0" max="30" value={s.retention_years ?? 0} onChange={(e) => setS({ ...s, retention_years: e.target.value })} className="w-24" data-testid="retention-years-input" /> <span className="text-sm">anni</span>
        <button onClick={saveYears} className="btn-secondary h-10 px-4" data-testid="retention-save-button">Salva</button>
        <button onClick={onShow} className="btn-secondary h-10 px-4" data-testid="retention-show-button">Mostra pratiche scadute</button>
      </div>
    </section>
  );
}

export default function Privacy() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState(new Set());
  const show = (data) => { setRes(data); setSel(new Set()); };
  const search = async (e) => { e.preventDefault(); try { show((await api.get("/privacy/search", { params: { q } })).data); } catch (err) { toast.error(formatApiError(err)); } };
  const expired = async () => {
    try { const { data } = await api.get("/privacy/expired"); if (!data.years) toast.info("Imposta prima un periodo di conservazione"); show(data); } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const body = () => Object.fromEntries(GROUPS.map(([key, field]) => [field, (res?.[key] || []).filter((r) => sel.has(r.id)).map((r) => r.id)]));
  const exportSel = async () => {
    try { saveBlob((await api.post("/privacy/export", body(), { responseType: "blob" })).data, "dati_cliente.json"); toast.success("Dati esportati"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const erase = async () => {
    const c = window.prompt("I dati personali selezionati (nome, telefono, indirizzo, documento e firma del cliente) saranno anonimizzati e non più recuperabili dall'app. Gli importi restano. Scrivi ANONIMIZZA per confermare.");
    if (c === null) return;
    try { const { data } = await api.post("/privacy/erase", { ...body(), confirm: c }); toast.success(`Anonimizzati: ${data.appointments} appuntamenti, ${data.impegnative} impegnative, ${data.customers} schede`); show(null); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-8 max-w-4xl">
      <div className="fade-up">
        <p className="eyebrow">Sicurezza</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Privacy e backup</h1>
        <p className="text-sm text-[var(--muted)] mt-2">Richieste dei clienti (accesso ai dati, cancellazione), periodo di conservazione e copie di sicurezza. Ogni operazione resta nel Registro attività.</p>
      </div>
      <section className={box} data-testid="privacy-requests">
        <h2 className="font-heading text-lg font-semibold">Richiesta di un cliente</h2>
        <form onSubmit={search} className="flex gap-2">
          <div className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-[var(--muted)]" /><Input className="pl-9" minLength={2} required placeholder="Nome o telefono del cliente" value={q} onChange={(e) => setQ(e.target.value)} data-testid="privacy-search-input" /></div>
          <button className="btn-primary h-10 px-4" data-testid="privacy-search-button">Cerca</button>
        </form>
        {res && <Results res={res} sel={sel} toggle={toggle} />}
        {res && !GROUPS.some(([k]) => (res[k] || []).length) && <p className="text-sm text-[var(--muted)]" data-testid="privacy-no-results">Nessun dato trovato</p>}
        {sel.size > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="privacy-actions">
            <button onClick={exportSel} className="btn-secondary h-10 px-4" data-testid="privacy-export-button"><FileDown className="h-4 w-4" /> Esporta dati ({sel.size})</button>
            <button onClick={erase} className="btn-secondary h-10 px-4 text-red-600" data-testid="privacy-erase-button"><UserX className="h-4 w-4" /> Anonimizza ({sel.size})</button>
          </div>
        )}
      </section>
      <RetentionCard onShow={expired} />
      <BackupPanel />
    </div>
  );
}
